import { supabase } from './supabase';

export interface BibleBookmark {
  id: string;
  user_id: string;
  book_id: string;
  chapter: number;
  verse?: number;
  note?: string;
  created_at: string;
}

/**
 * Busca todos os marcadores bíblicos do usuário atual
 */
export async function fetchUserBookmarks(): Promise<BibleBookmark[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return [];

    const { data, error } = await supabase
      .from('bible_bookmarks')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching bookmarks:', error);
    return [];
  }
}

/**
 * Adiciona um novo marcador bíblico
 */
export async function addBookmark(bookmark: Omit<BibleBookmark, 'id' | 'created_at'>): Promise<BibleBookmark | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Usuário não autenticado');

    const { data, error } = await supabase
      .from('bible_bookmarks')
      .insert({
        ...bookmark,
        user_id: session.user.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding bookmark:', error);
    return null;
  }
}

/**
 * Remove um marcador bíblico
 */
export async function removeBookmark(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('bible_bookmarks')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing bookmark:', error);
    return false;
  }
}

/**
 * Atualiza um marcador bíblico
 */
export async function updateBookmark(id: string, data: Partial<Omit<BibleBookmark, 'id' | 'user_id' | 'created_at'>>): Promise<BibleBookmark | null> {
  try {
    const { data: updatedData, error } = await supabase
      .from('bible_bookmarks')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return updatedData;
  } catch (error) {
    console.error('Error updating bookmark:', error);
    return null;
  }
}

/**
 * Verifica se um versículo está marcado
 */
export async function isVerseBookmarked(bookId: string, chapter: number, verse: number): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    const { data, error } = await supabase
      .from('bible_bookmarks')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('book_id', bookId)
      .eq('chapter', chapter)
      .eq('verse', verse)
      .maybeSingle();

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error('Error checking bookmark:', error);
    return false;
  }
}